import { useEffect, useMemo, useState } from "react";
import { Search, Check, X, ShieldCheck, Pencil, Save, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ROLE_LABELS } from "@/constants/roles";

export default function AdminRolesMatrixPage() {
  const [matrix, setMatrix] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [module, setModule] = useState("all");

  // Edit-mode state
  const [editMode, setEditMode] = useState(false);
  const [pending, setPending] = useState({}); // { `${role_code}:${permission_id}`: granted }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/role-permission-matrix");
      setMatrix(data.data.matrix);
      setRoles(data.data.roles);
      setPending({});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const modules = useMemo(() => {
    const s = new Set(matrix.map((m) => m.permission.module_name));
    return Array.from(s).sort();
  }, [matrix]);

  const filtered = useMemo(() => {
    let list = matrix;
    if (module !== "all") list = list.filter((m) => m.permission.module_name === module);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((m) =>
        m.permission.permission_code.toLowerCase().includes(s) ||
        (m.permission.description || "").toLowerCase().includes(s),
      );
    }
    return list;
  }, [matrix, module, search]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((row) => {
      const k = row.permission.module_name;
      g[k] = g[k] || [];
      g[k].push(row);
    });
    return g;
  }, [filtered]);

  const isGranted = (row, roleCode) => {
    const key = `${roleCode}:${row.permission.id}`;
    if (key in pending) return pending[key];
    return !!row.roles[roleCode];
  };
  const isDirty = (row, roleCode) => {
    const key = `${roleCode}:${row.permission.id}`;
    return key in pending && pending[key] !== !!row.roles[roleCode];
  };

  const toggle = (row, roleCode) => {
    if (roleCode === "super_admin") { toast.error("Super admin permissions are locked."); return; }
    const key = `${roleCode}:${row.permission.id}`;
    const current = key in pending ? pending[key] : !!row.roles[roleCode];
    setPending((p) => ({ ...p, [key]: !current }));
  };

  const changesCount = useMemo(() => {
    return Object.entries(pending).filter(([key, val]) => {
      const [rc, pid] = key.split(":");
      const row = matrix.find((m) => m.permission.id === pid);
      return row && !!row.roles[rc] !== !!val;
    }).length;
  }, [pending, matrix]);

  const save = async () => {
    const changes = [];
    Object.entries(pending).forEach(([key, val]) => {
      const [rc, pid] = key.split(":");
      const row = matrix.find((m) => m.permission.id === pid);
      if (row && !!row.roles[rc] !== !!val) {
        changes.push({ role_code: rc, permission_id: pid, granted: !!val });
      }
    });
    if (!changes.length) { setEditMode(false); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/role-permission-matrix", { changes });
      toast.success(data.message || "Permissions saved.");
      setEditMode(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const cancel = () => { setPending({}); setEditMode(false); };

  return (
    <DashboardLayout title="Roles & Permissions" breadcrumbs={["Platform", "Roles & Permissions"]}>
      <div className="space-y-4" data-testid={ADMIN.rolesRoot}>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search permissions…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)}
                     data-testid={ADMIN.rolesSearch} />
            </div>
            <Select value={module} onValueChange={setModule}>
              <SelectTrigger className="w-48" data-testid={ADMIN.rolesModuleFilter}><SelectValue placeholder="Module" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modules</SelectItem>
                {modules.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-2">
              {editMode ? (
                <>
                  <span className="text-xs font-semibold text-slate-600">
                    {changesCount} unsaved change{changesCount === 1 ? "" : "s"}
                  </span>
                  <button onClick={cancel} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="roles-cancel-btn">
                    <XCircle className="h-3.5 w-3.5" /> Cancel
                  </button>
                  <button onClick={save} disabled={saving || changesCount === 0} className="inline-flex items-center gap-1.5 rounded-md bg-[#329112] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#256b0d] disabled:opacity-50" data-testid="roles-save-btn">
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Save {changesCount > 0 ? `(${changesCount})` : ""}
                  </button>
                </>
              ) : (
                <>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                    <ShieldCheck className="h-3.5 w-3.5 text-[#329112]" /> Super Admin is locked
                  </span>
                  <button onClick={() => setEditMode(true)} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid="roles-edit-btn">
                    <Pencil className="h-3.5 w-3.5" /> Edit permissions
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingBlock label="Loading permission matrix…" />
        ) : filtered.length === 0 ? (
          <EmptyState title="No permissions match" description="Try clearing the module filter or search text." icon={ShieldCheck} />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 bg-slate-50/50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-6 py-3">Permission</th>
                    {roles.map((r) => (
                      <th key={r.id} className="px-4 py-3 text-center">
                        <div className="text-slate-900">{ROLE_LABELS[r.role_code] || r.role_name}</div>
                        {r.role_code === "super_admin" && <div className="mt-0.5 text-[9px] font-bold text-amber-600">LOCKED</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(grouped).map(([mod, rows]) => (
                    <tbody key={`grp-${mod}`}>
                      <tr className="bg-slate-50/50">
                        <td colSpan={roles.length + 1} className="px-6 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#329112]">
                          {mod}
                        </td>
                      </tr>
                      {rows.map((row) => (
                        <tr key={row.permission.id} className="border-t border-slate-100">
                          <td className="px-6 py-3">
                            <div className="text-sm font-semibold text-slate-900">{row.permission.permission_code}</div>
                            <div className="text-xs text-slate-500">{row.permission.description}</div>
                          </td>
                          {roles.map((r) => {
                            const granted = isGranted(row, r.role_code);
                            const dirty = isDirty(row, r.role_code);
                            const locked = r.role_code === "super_admin";
                            if (editMode && !locked) {
                              return (
                                <td key={r.id} className="px-4 py-3 text-center">
                                  <button
                                    type="button"
                                    onClick={() => toggle(row, r.role_code)}
                                    data-testid={`role-toggle-${r.role_code}-${row.permission.id}`}
                                    className={`inline-flex h-7 w-11 items-center rounded-full border-2 transition-colors ${granted ? "bg-[#329112] border-[#329112] justify-end" : "bg-slate-200 border-slate-200 justify-start"} ${dirty ? "ring-2 ring-amber-400" : ""}`}
                                    title={dirty ? "Unsaved change" : ""}
                                  >
                                    <span className="mx-0.5 h-5 w-5 rounded-full bg-white shadow" />
                                  </button>
                                </td>
                              );
                            }
                            return (
                              <td key={r.id} className="px-4 py-3 text-center">
                                {granted ? (
                                  <Check className="mx-auto h-5 w-5 text-[#329112]" />
                                ) : (
                                  <X className="mx-auto h-4 w-4 text-slate-300" />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
